import type { ChartDataset, ChartType } from 'chart.js'
import type { ShallowRef } from 'vue'
import type { MetricChartDataset, MetricOptions, MetricScales, SerieDataObject, StatusScales, WeekendScales } from '@/types/charts.types'
import type { DeepPartial } from '@/types/index.types'
import { TimeModes } from '@/types/charts.types'
import { DATES_FORMATS } from '@/utils/constants'
import { useDateTranslation } from './translation'

export function useChartExtendedOptions<T extends ChartType>(options: MaybeRef<MetricOptions<T>>, datasets: MaybeRef<MetricChartDataset[]>): {
  statusScales: ComputedRef<StatusScales | null>
  statusSeries: ComputedRef<DeepPartial<ChartDataset<'bar', SerieDataObject[]>> | null>
  weekendScales: ComputedRef<WeekendScales>
  weekendSeries: ComputedRef<DeepPartial<ChartDataset<'bar', SerieDataObject[]>>>
  showWeather: Readonly<ShallowRef<boolean>>
  showWeekend: Readonly<ShallowRef<boolean>>
  showStatus: Readonly<ShallowRef<boolean>>
  getExtendedOptions: ComputedRef<(scales: MetricScales<T>) => MetricScales<T>>
} {
  const optionsRef = ref(options)
  const datasetsRef = ref(datasets)

  const filtersStore = useFiltersStore()
  const { translateDate } = useDateTranslation()
  const { weathers } = useWeather()

  const showWeather = computedEager(
    () =>
      optionsRef.value.weather !== false
      && optionsRef.value.scales?.x?.type === 'time'
      && filtersStore.timeMode === TimeModes.DAY
      && ((optionsRef.value.isCompare && !filtersStore.comparativeRange)
        || !optionsRef.value.isCompare),
  )

  const showWeekend = computedEager(
    () =>
      optionsRef.value.weekend !== false
      && optionsRef.value.scales?.x?.type === 'time'
      && filtersStore.timeMode === TimeModes.DAY
      && ((optionsRef.value.isCompare && !filtersStore.comparativeRange)
        || !optionsRef.value.isCompare),
  )

  const showStatus = computedEager(() => {
    return datasetsRef.value?.length === 1 && Boolean(datasetsRef.value[0].status)
  })

  const statusScales = computed(() => {
    if (showStatus.value) {
      return {
        yStatus: {
          min: 0,
          max: 1,
          display: false,
          ticks: { display: false },
        },
        xStatus: {
          type: 'time',
          display: false,
          offset: !optionsRef.value.scales?.x?.stacked,
          time: {
            unit: TimeModes.DAY,
          },
        },
      } as StatusScales
    }

    return null
  })

  const statusSeries = computed<DeepPartial<ChartDataset<'bar', SerieDataObject[]>> | null>(() => {
    if (showStatus.value) {
      const { status } = datasetsRef.value[0]

      return {
        label: 'status',
        type: 'bar',
        backgroundColor: status?.map(getDataColorByStatus),
        yAxisID: 'yStatus',
        xAxisID: 'xStatus',
        barThickness: 'flex',
        categoryPercentage: 1,
        barPercentage: 1,
        data: status?.map(({ status, day }) => {
          const statusArray = (Array.isArray(status) && status) || [status]

          return {
            x: day,
            y: statusArray.length >= 1 ? 1 : 0,
          }
        }),
        order: 0,
        status,
      }
    }

    return null
  })

  const weekendScales = computed<WeekendScales>(() => {
    return {
      yWeekend: {
        min: 0,
        max: 1,
        display: false,
        ticks: { display: false },
      },
      xWeekend: {
        type: 'time',
        time: {
          unit: TimeModes.DAY,
        },
        display: false,
        offset: !optionsRef.value.scales?.x?.stacked,
      },
    } as WeekendScales
  })

  const weekendSeries = computed<DeepPartial<ChartDataset<'bar', SerieDataObject[]>>>(() => {
    return {
      label: CHART_WEEKEND_FILL_LABEL,
      type: 'bar',
      backgroundColor: 'rgba(5, 16, 43, 0.06)',
      xAxisID: 'xWeekend',
      yAxisID: 'yWeekend',
      categoryPercentage: 1,
      barPercentage: 1,
      data: getWeekendsSeriesData(
        filtersStore.dateRange.start,
        filtersStore.dateRange.end,
      ),
      order: 0,
    }
  })

  const cleanWeather = (v: string | string[]) => {
    const last = Array.isArray(v) ? v[v.length - 1] : v.slice(-1)
    const weathersRef = weathers.value
    let result = false
    let i = 0

    while (last !== weathersRef[i] && i < weathersRef.length) {
      i++
    }

    result = last === weathersRef[i]

    if (result && Array.isArray(v)) {
      const clean = v.slice(0, -1)
      return clean.length === 1 ? clean[0] : clean
    } else if (result) {
      return v.slice(0, -1)
    }

    return v
  }

  const weatherTicksCallback = computed(() => (value: string, index: number) => {
    const w = weathers.value?.[index]
    return w ? [cleanWeather(value), w] : value
  })

  const getExtendedOptions = computed(() => (scales: MetricScales<T>) => {
    const newScales = { ...scales }

    if ((showWeekend.value || showStatus.value) && newScales.y && 'position' in newScales.y) {
      (newScales.y as { position?: string }).position = 'left'
    }

    if (showWeekend.value) {
      Object.assign(newScales, weekendScales.value)
    }

    if (showStatus.value) {
      Object.assign(newScales, statusScales.value)
    }

    const tickCallback = scales.x.ticks?.callback

    // Prepare the format of the date in X axis
    if (!tickCallback && scales.x.type === 'time') {
      const timeMode = optionsRef.value.scales?.x?.time?.unit || filtersStore.timeMode
      const formatOfDate = timeMode === 'week'
        ? DATES_FORMATS.FULL
        : timeMode === 'month'
          ? DATES_FORMATS.MONTH
          : DATES_FORMATS.DAY

      const formatTickDate = (v: string | number) => translateDate(v, formatOfDate)

      // Add weather to X axis with the format of the date
      if (showWeather.value) {
        newScales.x.ticks = {
          ...newScales.x.ticks,
          callback: (...args) => {
            const value = formatTickDate(args[0])
            return weatherTicksCallback.value(value, args[1])
          },
        }
      } else {
        newScales.x.ticks = {
          ...newScales.x.ticks,
          callback: formatTickDate,
        }
      }

      return newScales
    }

    // Add weather to X axis
    if (showWeather.value) {
      newScales.x.ticks = {
        ...newScales.x.ticks,
        callback: (...args: any[]) => {
          if (tickCallback) {
            // @ts-expect-error tickCallback is a function with the same signature as the callback
            const value = tickCallback(...args) || args[0]
            return weatherTicksCallback.value(value as string, args[1] as number)
          }
          return args[0]
        },
      }
    }

    return newScales
  })

  return {
    statusScales,
    statusSeries,
    weekendScales,
    weekendSeries,
    showWeather,
    showWeekend,
    showStatus,
    getExtendedOptions,
  }
}
